import React, { useCallback, useMemo } from 'react';

import { ModalBody } from '..';
import { EikaModal } from '../../../../../../components/EikaModal';
import { PaymentState } from '../../../../../../components/transaction/Payment/form/state';
import {
    useEditOutbox,
    useInvalidateOutbox,
    useOutboxDetailsQuery,
    useRemoveOutbox,
} from '../../../../../../fetchHooks/useOutbox';
import type {
    PaymentItemDetails as OutboxItemDetails,
    PaymentItemId,
    UniquePaymentId,
} from '../../../../../../generated-types';
import { useSigningContext } from '../../../../../../signingContext/signingContext';
import { Actions } from '../../../../../../signingContext/signingTypes';
import { getOutboxMutateRequest } from '../../helpers';
import { useConfigureReceiverAccount, useOutboxFormContext } from '../../hooks';
import { OutboxEditViewFlow, OutboxStatus } from '../../types';

import './EditOutboxModalContent.scss';

interface Props {
    onClose: () => void;
    outboxId: UniquePaymentId;
    initialState: PaymentState;
    signSingleItem(itemId: PaymentItemId, outboxId: UniquePaymentId): void;
    outboxDetails: OutboxItemDetails;
    modalOpenState: [boolean, (value: boolean) => void];
}

const EditOutboxModalContent: React.FC<Props> = ({
    onClose,
    outboxId,
    initialState,
    signSingleItem,
    outboxDetails,
    modalOpenState: [open, setOpen],
}) => {
    useConfigureReceiverAccount(outboxDetails.receiver);

    const form = useOutboxFormContext();
    const { data } = useOutboxDetailsQuery(outboxDetails.paymentItemId);
    const { dispatch } = useSigningContext();

    const invalidateOutbox = useInvalidateOutbox();

    const {
        mutateAsync: update,
        status: updateStatus,
        isSuccess: updateSuccess,
        ...updateOutboxState
    } = useEditOutbox();

    const {
        mutate: deleteItem,
        status: deleteStatus,
        isSuccess: deleteSuccess,
        ...deleteOutboxState
    } = useRemoveOutbox();

    const closeAndReset = useCallback(async () => {
        if (deleteStatus !== 'idle' || updateStatus !== 'idle') {
            await invalidateOutbox();
        }
        deleteOutboxState.reset();
        updateOutboxState.reset();
        dispatch({ type: Actions.RESET });
        onClose();
    }, [dispatch, updateOutboxState, deleteOutboxState, onClose, deleteStatus, updateStatus, invalidateOutbox]);

    const sign = () => {
        if (!data?.paymentItemId) throw Error('Could not find payment');
        signSingleItem(data?.paymentItemId, outboxId);
        onClose();
    };

    const mutate = async (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (await form.checkFormValidity()) {
            await update(getOutboxMutateRequest(outboxDetails, form.state));
        }
    };

    const onDelete = useCallback(() => {
        deleteItem(outboxDetails.paymentItemId);
    }, [outboxDetails.paymentItemId, deleteItem]);

    const viewStatus: { flow: OutboxEditViewFlow; status: OutboxStatus } | undefined = useMemo(() => {
        if (deleteStatus !== 'idle') return { flow: 'deleting', status: deleteStatus };
        if (updateStatus !== 'idle') return { flow: 'updating', status: updateStatus };

        return undefined;
    }, [updateStatus, deleteStatus]);

    return (
        <EikaModal
            hideCloseButton={updateStatus === 'pending'}
            shouldExitOnEscape={updateStatus !== 'pending'}
            id="editOutboxPayment"
            timeOut={deleteSuccess || updateSuccess ? 2500 : undefined}
            onClose={closeAndReset}
            setOpen={setOpen}
            open={open}
            size="wide"
            desktopMinHeight="small"
            contentShouldStretch
            className="edit-outbox-modal-content__modal"
        >
            <ModalBody
                flow={viewStatus?.flow}
                status={viewStatus?.status}
                onClose={() => setOpen(false)}
                onMutate={mutate}
                onSign={sign}
                onDelete={onDelete}
                initialState={initialState}
            />
        </EikaModal>
    );
};

export default EditOutboxModalContent;
